import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

export const THEMES = {
  purple: { bar: "bg-[#4C1D95] text-white border-violet-200", sub: "text-violet-100", tag: "text-amber-200", icon: "text-amber-300", close: "text-violet-200" },
  gold: { bar: "bg-[#B45309] text-white border-amber-200", sub: "text-amber-50", tag: "text-yellow-100", icon: "text-yellow-200", close: "text-amber-100" },
  emerald: { bar: "bg-[#065F46] text-white border-emerald-200", sub: "text-emerald-50", tag: "text-lime-200", icon: "text-lime-200", close: "text-emerald-100" },
  rose: { bar: "bg-[#9F1239] text-white border-rose-200", sub: "text-rose-50", tag: "text-amber-100", icon: "text-amber-100", close: "text-rose-100" },
};

export const useAnnouncement = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/settings/announcement").then((r) => setData(r.data)).catch(() => setData({ enabled: false })); }, []);
  return data;
};

export const AnnouncementPreview = ({ data, onCta, onClose, fixed = false }) => {
  const t = THEMES[data.theme] || THEMES.purple;
  return (
    <div data-testid={fixed ? "announcement-bar" : "announcement-preview"} className={`${fixed ? "fixed inset-x-0 top-[72px] z-40" : ""} border-b ${t.bar}`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 sm:gap-4 sm:px-6 sm:py-2.5 lg:px-10">
        <p className="flex min-w-0 items-center gap-x-3 gap-y-1 text-sm sm:flex-wrap">
          <span data-testid="announcement-title" className="flex shrink-0 items-center gap-1.5 font-display font-semibold tracking-wide"><Sparkles className={`h-4 w-4 shrink-0 ${t.icon}`} /> {data.title}</span>
          {data.message && <span className={`hidden sm:inline ${t.sub}`}>{data.message}</span>}
          {data.tag && <span className={`truncate font-mono2 text-[9px] uppercase tracking-[0.15em] sm:text-[10px] sm:tracking-[0.2em] ${t.tag}`}>{data.tag}</span>}
        </p>
        <div className="flex shrink-0 items-center gap-3">
          {data.cta_label && <button type="button" data-testid="announcement-cta" onClick={onCta} className={`hidden items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.2em] transition-colors hover:text-white sm:flex ${t.tag}`}>{data.cta_label} <ArrowRight className="h-3 w-3" /></button>}
          {onClose && <button type="button" data-testid="announcement-close" onClick={onClose} aria-label="Dismiss" className={`transition-colors hover:text-white ${t.close}`}><X className="h-4 w-4" /></button>}
        </div>
      </div>
    </div>
  );
};

export const AnnouncementBar = ({ data }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const key = `announce_closed_${data?.updated_at || "default"}`;
  const [closed, setClosed] = useState(false);
  useEffect(() => { setClosed(sessionStorage.getItem(key) === "1"); }, [key]);
  if (!data?.enabled || closed) return null;

  const go = () => {
    const target = data.cta_target || "#opportunities";
    if (target.startsWith("/")) { navigate(target); return; }
    if (location.pathname !== "/") navigate("/");
    setTimeout(() => scrollToId(target), 50);
  };
  return <AnnouncementPreview data={data} fixed onCta={go} onClose={() => { sessionStorage.setItem(key, "1"); setClosed(true); }} />;
};
